// SAFE-WRITE-BEGIN: frontend/src/pages/valuation/_useDraftExtras.ts
/**
 * frontend/src/pages/valuation/_useDraftExtras.ts
 *
 * VAL-SAVE-ON-TYPE-2 — two kinds of typing that save to the working draft as
 * they happen (debounced, `useAutosave`) and come back when the user returns:
 * the sign-off reason, and the "I'm not sure" answers on the driver questions.
 * Both ride in the draft's `tuning` column (`signoff_reason`,
 * `deferred_questions`); the server merges keys, so a Methodology save never
 * drops them.
 *
 * Touched-field tracking (VAL-DRAFT-OVERWRITE-1): the saved value is applied
 * ONCE, when the draft is known to exist (`enabled`), and never over something
 * the user already typed. Autosave starts at the same moment, so the hydrated
 * value is the baseline and is not written back.
 *
 * `saveValuationDraft` is called directly, not through `useValuationDraft.save`
 * — that one bumps `updatedAt`, which re-runs the Sign-off preflight on every
 * keystroke's save.
 *
 * The sign-off still sends only the signer's own reason: nothing here is read
 * by the server to sign (VAL-SIGNOFF-REASON-DISTINCT-1).
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { saveValuationDraft } from '@/api/valuationB3'
import { useAutosave } from './_useAutosave'
import type { DeferredAttachment } from './_DriverQuestionDefer'

export interface DeferredPersist {
  valuationId: string | undefined
  /** question id -> attached filename ('' = deferred, nothing attached). */
  saved: Record<string, string> | undefined
  /** True once the draft has loaded and exists. */
  enabled: boolean
}

export function useSignoffReason(
  valuationId: string | undefined,
  saved: string | undefined,
  enabled: boolean,
): [string, (value: string) => void] {
  const [reason, setReasonRaw] = useState('')
  const [ready, setReady] = useState(false)
  const touched = useRef(false)
  const latest = useRef(reason)
  latest.current = reason

  const setReason = useCallback((value: string) => {
    touched.current = true
    setReasonRaw(value)
  }, [])

  const save = useCallback(
    async (value: string): Promise<string | null> => {
      if (!valuationId) return null
      try {
        await saveValuationDraft(valuationId, { tuning: { signoff_reason: value } })
        return null
      } catch {
        return 'Could not save your reason.'
      }
    },
    [valuationId],
  )
  useEffect(() => {
    if (!enabled || ready) return
    if (!touched.current && saved) setReasonRaw(saved)
    // Typed before the draft existed: that is a change, not a baseline.
    if (touched.current) void save(latest.current)
    setReady(true)
  }, [enabled, ready, saved, save])

  useAutosave(reason, save, { enabled: enabled && ready })
  return [reason, setReason]
}

type Setter<T> = (update: (prev: T) => T) => void

export function useDeferredQuestions(persist: DeferredPersist | undefined): {
  deferred: Record<string, boolean>
  setDeferred: Setter<Record<string, boolean>>
  attachments: Record<string, DeferredAttachment>
  setAttachments: Setter<Record<string, DeferredAttachment>>
} {
  const [deferred, setDeferredRaw] = useState<Record<string, boolean>>({})
  const [attachments, setAttachmentsRaw] = useState<Record<string, DeferredAttachment>>({})
  const [ready, setReady] = useState(false)
  const touched = useRef(false)
  const enabled = !!persist?.enabled

  const setDeferred: Setter<Record<string, boolean>> = useCallback(update => {
    touched.current = true
    setDeferredRaw(update)
  }, [])
  const setAttachments: Setter<Record<string, DeferredAttachment>> = useCallback(update => {
    touched.current = true
    setAttachmentsRaw(update)
  }, [])

  const payload: Record<string, string> = {}
  for (const [q, on] of Object.entries(deferred)) if (on) payload[q] = attachments[q]?.name ?? ''

  const valuationId = persist?.valuationId
  const save = useCallback(
    async (value: Record<string, string>): Promise<string | null> => {
      if (!valuationId) return null
      try {
        await saveValuationDraft(valuationId, { tuning: { deferred_questions: value } })
        return null
      } catch {
        return 'Could not save your answers.'
      }
    },
    [valuationId],
  )
  const latest = useRef(payload)
  latest.current = payload

  useEffect(() => {
    if (!enabled || ready) return
    const saved = persist?.saved
    if (!touched.current && saved) {
      setDeferredRaw(Object.fromEntries(Object.keys(saved).map(q => [q, true])))
      setAttachmentsRaw(Object.fromEntries(
        Object.entries(saved).filter(([, name]) => name).map(([q, name]) => [q, { name }]),
      ))
    }
    // Deferred before the draft existed: that is a change, not a baseline.
    if (touched.current) void save(latest.current)
    setReady(true)
  }, [enabled, ready, persist?.saved, save])

  useAutosave(payload, save, { enabled: enabled && ready })
  return { deferred, setDeferred, attachments, setAttachments }
}
// SAFE-WRITE-EOF: frontend/src/pages/valuation/_useDraftExtras.ts
